import { useRef, useState } from 'react';
import { CheckCircle2, ListChecks } from 'lucide-react';
import { toast } from 'sonner';
import { useUser } from '@/contexts/UserContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { marcarTareaApi } from '@/utils/certificadosApi';
import { formatearFecha } from '@/utils/avisoCertificado';
import type { DigitalCertificate } from '@/types';

interface Props {
  archivados: DigitalCertificate[];
  /** Se llama tras marcar una tarea: el padre recarga la lista. */
  onCambio: () => void;
}

type Tarea = 'bajaFace' | 'revocacionFnmt';

const TAREAS: Array<{
  tarea: Tarea;
  campo: 'bajaFaceEn' | 'revocacionFnmtEn';
  texto: string;
  etiqueta: string;
  detalle?: string;
}> = [
  {
    tarea: 'bajaFace',
    campo: 'bajaFaceEn',
    texto: 'Quita el certificado anterior de SantaFactura en FACe',
    etiqueta: 'Marcar como hecho: quitar de FACe',
  },
  {
    tarea: 'revocacionFnmt',
    campo: 'revocacionFnmtEn',
    texto: 'Revoca el certificado anterior en la FNMT (recomendado)',
    etiqueta: 'Marcar como hecho: revocar en la FNMT',
    detalle:
      'Las exportaciones y copias de seguridad antiguas de la base de datos pueden contener la clave del ' +
      'certificado anterior con el cifrado de antes. Una vez revocado, esas copias dejan de servir.',
  },
];

/**
 * Tarjeta persistente de tareas posteriores a la renovación: se queda mientras
 * algún certificado archivado tenga alguna sin marcar.
 */
export function TarjetaTareasTrasRenovacion({ archivados, onCambio }: Props) {
  const { fetchWithAuth } = useUser();
  const [marcando, setMarcando] = useState<Set<string>>(new Set());
  const marcandoRef = useRef<Set<string>>(new Set());

  const conPendientes = archivados.filter((c) =>
    c.tareasTrasRenovar && (!c.tareasTrasRenovar.bajaFaceEn || !c.tareasTrasRenovar.revocacionFnmtEn)
  );
  if (conPendientes.length === 0) return null;

  const marcar = async (id: string, tarea: Tarea) => {
    const clave = `${id}:${tarea}`;
    if (marcandoRef.current.has(clave)) return;
    marcandoRef.current.add(clave);
    setMarcando(new Set(marcandoRef.current));
    let marcada = false;
    try {
      const resultado = await marcarTareaApi(fetchWithAuth, id, tarea);
      if (resultado.ok) {
        marcada = true;
      } else {
        toast.error(resultado.error);
      }
    } catch {
      toast.error('No se pudo marcar la tarea.');
    } finally {
      marcandoRef.current.delete(clave);
      setMarcando(new Set(marcandoRef.current));
    }
    // Fuera del try: si el padre falla al recargar, la tarea ya se marcó y no hay que decir lo contrario.
    if (marcada) {
      try {
        onCambio();
      } catch (error) {
        console.error('Error al recargar tras marcar la tarea:', error);
      }
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ListChecks className="h-5 w-5" aria-hidden="true" />
          Tareas tras la renovación
        </CardTitle>
        <CardDescription>
          Cierra el ciclo del certificado anterior para que no quede ningún acceso abierto.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {conPendientes.map((certificado) => (
          <section key={certificado.id} className="space-y-3">
            <h3 className="font-semibold">Certificado anterior: {certificado.nombre}</h3>
            <ul className="space-y-3">
              {TAREAS.map(({ tarea, campo, texto, etiqueta, detalle }) => {
                const hechaEn = certificado.tareasTrasRenovar?.[campo];
                return (
                  <li key={tarea} className="flex flex-wrap items-start justify-between gap-2">
                    <div className="max-w-xl">
                      <p>{texto}</p>
                      {detalle && <p className="text-sm text-muted-foreground">{detalle}</p>}
                    </div>
                    {hechaEn ? (
                      <span className="flex items-center gap-1 text-sm text-green-700">
                        <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
                        Hecho el {formatearFecha(hechaEn)}
                      </span>
                    ) : (
                      <Button size="sm" variant="outline" aria-label={`${etiqueta} (${certificado.nombre})`} onClick={() => marcar(certificado.id, tarea)}
                        disabled={marcando.has(`${certificado.id}:${tarea}`)}>
                        Hecho
                      </Button>
                    )}
                  </li>
                );
              })}
            </ul>
          </section>
        ))}
      </CardContent>
    </Card>
  );
}
